import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  X, CheckCircle2, XCircle, ArrowLeft, Video, RefreshCw,
  ArrowUpDown, Power, ShieldCheck, Cpu, TrendingUp,
  Target, ClipboardList, Heart, Thermometer, Droplets,
  Activity, SmilePlus, Star, Lock, ChevronDown, Calendar, User,
} from 'lucide-react';
import { toast } from 'sonner';

import PatientPanelHeader from '@/components/PatientPanelHeader';

/* ──────── MOCK CLINICAL DATA ──────── */
const TELEMONITORAGGIO = [
  { label: 'SpO2', unita: '%', valori: [94, 95, 96, 95], soglia: 92, color: '#06B6D4' },
  { label: 'PA Sist.', unita: 'mmHg', valori: [155, 148, 142, 138], soglia: 150, color: '#8B5CF6' },
  { label: 'PA Diast.', unita: 'mmHg', valori: [92, 88, 85, 82], soglia: 90, color: '#A78BFA' },
  { label: 'FC', unita: 'bpm', valori: [88, 82, 78, 76], soglia: 100, color: '#F59E0B' },
];
const SETTIMANE = ['Sett. 1', 'Sett. 2', 'Sett. 3', 'Sett. 4'];

const PROMS_PREMS = [
  { label: 'Variazione Dolore (VAS)', valore: -30, tipo: 'delta', positivo: true },
  { label: 'Barthel Index', valore: 65, max: 100, tipo: 'score', positivo: true },
  { label: 'Rischio Cadute (Tinetti)', valore: 14, max: 28, tipo: 'score', positivo: false },
  { label: 'Customer Satisfaction (PREMs)', valore: 4.8, max: 5, tipo: 'rating', positivo: true },
];

const OBIETTIVI = [
  { label: 'Miglioramento Mobilita', stato: 'raggiunto' },
  { label: 'Autonomia nella Vestizione', stato: 'raggiunto' },
  { label: 'Gestione Glicemia', stato: 'parziale' },
  { label: 'Riduzione Rischio Cadute', stato: 'non_raggiunto' },
  { label: 'Compliance Terapeutica', stato: 'raggiunto' },
];

const INTERRAI = [
  { item: 'Declino Cognitivo (CPS)', iniziale: '2 - Lieve', odierna: '2 - Lieve', delta: 'Invariato' },
  { item: 'ADL Gerarchia', iniziale: '4 - Dipendenza', odierna: '3 - Estesa assist.', delta: 'Migliorato' },
  { item: 'Dolore (Pain Scale)', iniziale: '3 - Grave', odierna: '1 - Lieve', delta: 'Migliorato' },
  { item: 'CHESS Score', iniziale: '3 - Alto', odierna: '2 - Medio', delta: 'Migliorato' },
  { item: 'Rischio Cadute (J5c)', iniziale: 'Si', odierna: 'Si', delta: 'Invariato' },
];

const obiettivoConfig = {
  raggiunto: { label: 'Raggiunto', bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  parziale: { label: 'Parziale', bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  non_raggiunto: { label: 'Non raggiunto', bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
};

const deltaConfig = {
  Migliorato: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400' },
  Invariato: { bg: 'bg-slate-100 dark:bg-slate-700/40', text: 'text-slate-700 dark:text-slate-300' },
  Peggiorato: { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400' },
};

/* ──────────── LEFT PANEL ──────────── */
function LeftPanel() {
  return (
    <div className="flex flex-col gap-6 overflow-y-auto pr-2 h-full">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Quadro Clinico e Dati di Esito</p>

      {/* 1. Telemonitoraggio */}
      <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-slate-50 dark:bg-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-cyan-500" />
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-200">Andamento Telemonitoraggio</span>
          </div>
          <div className="flex flex-col gap-4">
            {TELEMONITORAGGIO.map((param) => {
              const max = Math.max(...param.valori, param.soglia) * 1.15;
              return (
                <div key={param.label}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">{param.label} ({param.unita})</span>
                    <span className="text-xs font-bold" style={{ color: param.color }}>{param.valori[param.valori.length - 1]} {param.unita}</span>
                  </div>
                  <div className="flex items-end gap-1 h-10">
                    {param.valori.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-full rounded-t-sm" style={{ height: `${(v / max) * 100}%`, backgroundColor: param.color, opacity: 0.4 + (i * 0.2) }} />
                        <span className="text-[8px] text-slate-500 dark:text-slate-400">{SETTIMANE[i].replace('Sett. ', 'S')}</span>
                      </div>
                    ))}
                    {/* Soglia */}
                    <div className="absolute left-0 right-0" style={{ bottom: `${(param.soglia / max) * 100}%` }}>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* 2. PROMs/PREMs */}
      <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-slate-50 dark:bg-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <SmilePlus className="w-4 h-4 text-violet-500" />
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-200">Esiti PROMs e PREMs</span>
          </div>
          <div className="flex flex-col gap-3.5">
            {PROMS_PREMS.map((p) => (
              <div key={p.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">{p.label}</span>
                  {p.tipo === 'delta' && (
                    <Badge className={`${p.positivo ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'} border-0 text-[10px] font-bold`}>{p.valore > 0 ? '+' : ''}{p.valore}%</Badge>
                  )}
                  {p.tipo === 'rating' && (
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{p.valore}/{p.max}</span>
                    </div>
                  )}
                  {p.tipo === 'score' && (
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{p.valore}/{p.max}</span>
                  )}
                </div>
                {p.tipo === 'score' && (
                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className={`h-full rounded-full ${p.positivo ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{ width: `${(p.valore / p.max) * 100}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 3. Verifica Obiettivi */}
      <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-slate-50 dark:bg-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-200">Scheda Verifica Obiettivi</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {OBIETTIVI.map((ob) => {
              const cfg = obiettivoConfig[ob.stato];
              return (
                <div key={ob.label} className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-slate-700/30 border border-slate-200 dark:border-slate-600/30">
                  <span className="text-xs font-medium text-slate-900 dark:text-slate-100">{ob.label}</span>
                  <Badge className={`${cfg.bg} ${cfg.text} border ${cfg.border} text-[10px] font-semibold`}>{cfg.label}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* 4. Raffronto InterRAI HC */}
      <Card className="border border-slate-200 dark:border-slate-700 shadow-none bg-slate-50 dark:bg-slate-800">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-200">Raffronto InterRAI HC</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="interrai-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600/40">
                  <th className="pb-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">Item</th>
                  <th className="pb-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase text-center">Iniziale</th>
                  <th className="pb-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase text-center">Odierna</th>
                  <th className="pb-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase text-center">Delta</th>
                </tr>
              </thead>
              <tbody>
                {INTERRAI.map((row) => {
                  const dc = deltaConfig[row.delta] || deltaConfig.Invariato;
                  return (
                    <tr key={row.item} className="border-b border-slate-200 dark:border-slate-600/20">
                      <td className="py-2.5 text-xs font-medium text-slate-900 dark:text-slate-100">{row.item}</td>
                      <td className="py-2.5 text-[10px] text-slate-500 dark:text-slate-400 text-center">{row.iniziale}</td>
                      <td className="py-2.5 text-[10px] text-slate-900 dark:text-slate-100 font-semibold text-center">{row.odierna}</td>
                      <td className="py-2.5 text-center">
                        <Badge className={`${dc.bg} ${dc.text} border-0 text-[10px] font-semibold`}>{row.delta}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ──────────── RIGHT PANEL — SCHEDULING (3 BOX) ──────────── */
const PROFESSIONI = [
  { id: 'medico-distretto', label: 'Medico di Distretto (Responsabile Clinico)' },
  { id: 'geriatra', label: 'Geriatra' },
  { id: 'fisiatra', label: 'Fisiatra' },
  { id: 'neurologo', label: 'Neurologo' },
  { id: 'psichiatra', label: 'Psichiatra' },
  { id: 'palliativista', label: 'Palliativista' },
  { id: 'psicologo', label: 'Psicologo' },
  { id: 'fisioterapista', label: 'Fisioterapista' },
  { id: 'assistente-sociale', label: 'Assistente Sociale' },
];

const NOME_MAP = {
  'medico-distretto': 'Dott. Franco Lombardi',
  'geriatra': 'Dott.ssa Maria Conte',
  'fisiatra': 'Dott.ssa Elena Tosi',
  'neurologo': 'Dott.ssa Sofia Gatti',
  'psichiatra': 'Dott. Enrico Bassi',
  'palliativista': 'Dott.ssa Paola Ferro',
  'psicologo': 'Dott. Claudio Marini',
  'fisioterapista': 'Ft. Andrea Colombo',
  'assistente-sociale': 'Dott.ssa Barbara Villa',
};

const TIME_SLOTS = ['09:00', '10:00', '11:00'];
const GIORNI_SETT = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'];
const DISABLED_DAYS = new Set([1, 2, 7, 8, 13, 14, 15, 21]);

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

function SchedulingPanel({ onClose, editData }) {
  const [selectedRoles, setSelectedRoles] = useState(editData?.selectedRoleIds || []);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(editData?.selectedDay || null);
  const [selectedTime, setSelectedTime] = useState(editData?.time || null);
  const [calMonth] = useState(3);
  const [calYear] = useState(2026);

  const calendarCells = buildCalendar(calYear, calMonth);

  const toggleRole = (id) => {
    setSelectedRoles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const removeRole = (id) => setSelectedRoles(prev => prev.filter(x => x !== id));

  const dayName = selectedDay ? ['DOM', 'LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB'][new Date(calYear, calMonth, selectedDay).getDay()] : '';

  const isDayDisabled = (day) => {
    if (!day) return true;
    const dow = new Date(calYear, calMonth, day).getDay();
    if (dow === 0 || dow === 6) return true;
    if (DISABLED_DAYS.has(day)) return true;
    return false;
  };

  return (
    <div className="flex flex-col gap-5" data-testid="scheduling-panel">
      <div>
        <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">Pianificazione Rivalutazione</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">Convoca l'équipe UVMD e fissa la data della valutazione.</p>
      </div>

      {/* ── STEP 1: Dropdown Multi-Select Ruoli ── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 overflow-visible" data-testid="equipe-card">
        <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Équipe Valutazione UVMD</h3>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-3">Seleziona le figure professionali (ruoli)</p>

        {/* Dropdown trigger */}
        <div className="relative" data-testid="dropdown-wrapper">
          <button
            onClick={() => setDropdownOpen(prev => !prev)}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-left flex items-center justify-between transition-colors hover:border-slate-300 dark:hover:border-slate-600"
            data-testid="dropdown-trigger"
          >
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {selectedRoles.length > 0 ? `${selectedRoles.length} professione${selectedRoles.length > 1 ? 'i' : ''} selezionata${selectedRoles.length > 1 ? 'e' : ''}` : 'Seleziona professioni...'}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg z-10 max-h-64 overflow-y-auto" data-testid="dropdown-menu">
              {PROFESSIONI.map(p => {
                const checked = selectedRoles.includes(p.id);
                return (
                  <label key={p.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-testid={`dropdown-opt-${p.id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleRole(p.id)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/30" />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{p.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected pills */}
        {selectedRoles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="selected-roles-pills">
            {selectedRoles.map(id => {
              const prof = PROFESSIONI.find(p => p.id === id);
              return (
                <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                  {prof?.label}
                  <button onClick={() => removeRole(id)} className="w-4 h-4 rounded-full hover:bg-emerald-200 dark:hover:bg-emerald-800 flex items-center justify-center transition-colors" data-testid={`remove-${id}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ── STEP 2: Calendario (visibile solo dopo selezione ruoli) ── */}
      {selectedRoles.length > 0 && (
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5" data-testid="calendario-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Calendario Disponibilità</h3>
          <span className="text-[10px] text-slate-400 dark:text-slate-500">Incrociati per {selectedRoles.length} figure</span>
        </div>

        <div className="text-center mb-3">
          <p className="text-sm font-bold text-slate-800 dark:text-white">{MESI[calMonth]} {calYear}</p>
        </div>

        <div className="grid grid-cols-7 gap-0 mb-1">
          {GIORNI_SETT.map(d => (
            <div key={d} className="text-center text-[9px] font-bold text-slate-400 dark:text-slate-500 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0" data-testid="calendar-grid">
          {calendarCells.map((day, i) => {
            if (day === null) return <div key={`e-${i}`} />;
            const isSel = day === selectedDay;
            const disabled = isDayDisabled(day);
            return (
              <button
                key={day}
                onClick={() => !disabled && setSelectedDay(day)}
                disabled={disabled}
                className={`w-full aspect-square flex items-center justify-center rounded-full text-xs font-medium transition-all
                  ${isSel ? 'bg-emerald-600 text-white font-bold shadow-md' : disabled ? 'text-slate-300 dark:text-slate-600 opacity-30 cursor-not-allowed' : 'text-slate-700 dark:text-slate-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'}`}
                data-testid={`cal-day-${day}`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* STEP 3: Time slots (visibile solo dopo selezione giorno) */}
        {selectedDay && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="time-selector">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-800 dark:text-white">{dayName} {selectedDay} {MESI[calMonth].substring(0, 3).toUpperCase()}</span>
              <span className="text-[10px] text-slate-400">Fasce disponibili</span>
            </div>
            <div className="flex gap-2">
              {TIME_SLOTS.map(t => (
                <button key={t} onClick={() => setSelectedTime(t)} className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${selectedTime === t ? 'bg-emerald-600 text-white shadow-md' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-emerald-300 dark:hover:border-emerald-700'}`} data-testid={`slot-${t.replace(':','-')}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── STEP 4: Riepilogo + CTA (visibile solo dopo selezione orario) ── */}
      {selectedTime && (
      <>
        <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-xl border border-emerald-200 dark:border-emerald-800/40 p-5" data-testid="riepilogo-card">
          <h3 className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-3">Équipe Individuata</h3>

          <div className="bg-white dark:bg-slate-900 rounded-lg border border-emerald-200 dark:border-emerald-800/40 px-4 py-3 flex items-center gap-3 mb-4" data-testid="riepilogo-data">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
              <Calendar className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-800 dark:text-white">Visita UVMD programmata per:</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">{dayName.toLowerCase()} {selectedDay} {MESI[calMonth].toLowerCase()} alle ore {selectedTime}</p>
            </div>
          </div>

          <div className="flex flex-col gap-2" data-testid="riepilogo-professionisti">
            {selectedRoles.map(id => {
              const prof = PROFESSIONI.find(p => p.id === id);
              const nome = NOME_MAP[id] || 'Da assegnare';
              return (
                <div key={id} className="bg-white dark:bg-slate-900 rounded-lg border border-emerald-200 dark:border-emerald-800/40 px-4 py-3 flex items-center gap-3" data-testid={`prof-${id}`}>
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                    <User className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-white">{nome}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{prof?.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button onClick={() => {
          const dayLabel = `${dayName} ${selectedDay} ${MESI[calMonth]}`;
          const teamData = selectedRoles.map(id => {
            const prof = PROFESSIONI.find(p => p.id === id);
            return { id, label: prof?.label || id, nome: NOME_MAP[id] || 'Da assegnare' };
          });
          toast.success(`Équipe convocata con successo per il ${dayLabel} alle ore ${selectedTime}`);
          setTimeout(() => onClose?.({ isPlanned: true, date: dayLabel, time: selectedTime, team: teamData, selectedDay, selectedRoleIds: selectedRoles }), 600);
        }} className="w-full py-3.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-testid="btn-pianifica-convoca">
          <CheckCircle2 className="w-4 h-4" /> Pianifica e Convoca Équipe
        </button>
      </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN EXPORT — PaiReevaluationScreen (Scheduling Mode)
   ════════════════════════════════════════════ */
export default function PaiReevaluationScreen({ open, onClose, patient, editData }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 dark:bg-slate-950 flex flex-col" data-testid="pai-reevaluation-screen">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors" data-testid="reeval-close">
            <ArrowLeft className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </button>
          <div>
            <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100">Pianificazione Rivalutazione PAI</h1>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">{patient?.nome} {patient?.cognome} — {patient?.diagnosi}</p>
          </div>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors" data-testid="reeval-close-x">
          <X className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col p-6 md:p-8 max-w-[1600px] mx-auto w-full">
        <div className="flex-shrink-0">
          <PatientPanelHeader patient={patient} />
        </div>

        {/* Split Screen */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Colonna Sinistra — Dati Clinici (invariata) */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-sm overflow-y-auto">
            <LeftPanel />
          </div>
          {/* Colonna Destra — Scheduling */}
          <div className="bg-white dark:bg-slate-800/50 p-6 rounded-3xl shadow-sm overflow-y-auto pb-24">
            <SchedulingPanel onClose={onClose} editData={editData} />
          </div>
        </div>
      </div>
    </div>
  );
}

